import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { AdminService, AsrProxyAdminService, GetAllPromptsResponseDto, PromptsService } from 'app/API';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';

export const AdminAIModelChoiceModal = ({ organization }: TProps) => {
  const { closeModal } = useModalContext();
  const { call } = useAPI();

  const [isLoading, setIsLoading] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [bulkModel, setBulkModel] = useState('');
  const [asrDefaultModel, setAsrDefaultModel] = useState('');
  const [asrModel, setAsrModel] = useState('');
  const [asrModels, setAsrModels] = useState<string[]>([]);
  const [selectedAsrModel, setSelectedAsrModel] = useState('');
  const [versions, setVersions] = useState<string[]>([]);
  const [allPrompts, setAllPrompts] = useState<GetAllPromptsResponseDto[]>([]);
  const [promptsConfigMap, setPromptsConfigMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    setIsLoading(true);

    async function fetchData() {
      try {
        const [modelsResult, promptsResult, promptConfigsResult, asrModelsResult, asrConfigResult] =
          await Promise.allSettled([
            call(() => AdminService.getAvailableOpenAiModels()),
            call(() => PromptsService.getAll()),
            call(() => PromptsService.getPromptConfigsForOrg({ oid: organization.id })),
            call(() => AsrProxyAdminService.models()),
            call(() => AsrProxyAdminService.getOrganizationConfig({ organizationId: organization.id })),
          ]);

        const nextModels = (modelsResult.status === 'fulfilled' ? modelsResult.value : undefined) ?? [];
        const nextPrompts = (promptsResult.status === 'fulfilled' ? promptsResult.value : undefined) ?? [];
        const nextPromptConfigs =
          (promptConfigsResult.status === 'fulfilled' ? promptConfigsResult.value : undefined) ?? [];
        const fetchedAsrModels = asrModelsResult.status === 'fulfilled' ? asrModelsResult.value : undefined;
        const fetchedAsrConfig = asrConfigResult.status === 'fulfilled' ? asrConfigResult.value : undefined;
        const settledSections: Array<[string, PromiseSettledResult<unknown>]> = [
          ['LLM models', modelsResult],
          ['prompts', promptsResult],
          ['prompt model settings', promptConfigsResult],
          ['ASR models', asrModelsResult],
          ['ASR model settings', asrConfigResult],
        ];
        const failedSections = settledSections
          .filter(([, result]) => result.status === 'rejected')
          .map(([section]) => section);

        if (failedSections.length > 0) {
          toast.error(`Failed to load ${failedSections.join(', ')}. Reopen the modal to retry.`);
        }

        const map = new Map<string, string>(nextPrompts.map((prompt) => [prompt.slug, '']));

        nextPromptConfigs.forEach((config) => {
          map.set(config.promptSlug, config.model);
        });

        setVersions(nextModels);
        setAllPrompts(nextPrompts);
        setPromptsConfigMap(map);
        setAsrDefaultModel(fetchedAsrModels?.default_model ?? '');
        setAsrModels(fetchedAsrModels?.models.map((model) => model.model_name) ?? []);
        const organizationAsrModel = fetchedAsrConfig?.model ?? '';
        setAsrModel(organizationAsrModel);
        setSelectedAsrModel(organizationAsrModel);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [call, organization.id]);

  const updatePromptModelForOrg = async (slug: string, model: string) => {
    const response = await call(
      () =>
        PromptsService.updatePromptModelForOrganization({
          oid: organization.id,
          slug,
          requestBody: {
            model,
          },
        }),
      {
        dontRedirectOnError: true,
        onError: (error) => toast.error(error),
      },
    );

    return typeof response !== 'undefined';
  };

  const updateAsrModelForOrg = async (model: string) => {
    setIsLoading(true);
    toast.loading(model ? 'Updating ASR model...' : 'Restoring default ASR model...', { id: 'asr-model' });
    let isUpdated = false;

    try {
      const requestOptions = {
        dontRedirectOnError: true,
        onError: (error: string) => toast.error(error),
        onSuccess: () => {
          isUpdated = true;
        },
      };

      if (model) {
        await call(
          () =>
            AsrProxyAdminService.setOrganizationModel({
              organizationId: organization.id,
              requestBody: { model },
            }),
          requestOptions,
        );
      } else {
        await call(
          () => AsrProxyAdminService.deleteOrganizationConfig({ organizationId: organization.id }),
          requestOptions,
        );
      }

      if (isUpdated) {
        setAsrModel(model);
        toast.success(model ? 'ASR model updated successfully' : 'Default ASR model restored');
      }
    } finally {
      toast.remove('asr-model');
      setIsLoading(false);
    }
  };

  const handleBulkUpdate = async (model: string) => {
    if (!allPrompts.length) {
      return;
    }

    setIsLoading(true);
    toast.loading(model ? 'Updating all model selections...' : 'Clearing all model selections...', {
      id: 'model-bulk',
    });

    try {
      const successfulSlugs: string[] = [];

      for (const prompt of allPrompts) {
        const isUpdated = await updatePromptModelForOrg(prompt.slug, model);
        if (isUpdated) {
          successfulSlugs.push(prompt.slug);
        }
      }

      setPromptsConfigMap((prev) => {
        const updated = new Map(prev);
        successfulSlugs.forEach((slug) => {
          updated.set(slug, model);
        });
        return updated;
      });

      if (successfulSlugs.length === allPrompts.length) {
        toast.success(model ? 'All models were updated' : 'All models were unset');
      } else {
        toast.error(`${allPrompts.length - successfulSlugs.length} model settings failed to update`);
      }
    } finally {
      toast.remove('model-bulk');
      setIsLoading(false);
    }
  };

  const asrModelOptions =
    selectedAsrModel && !asrModels.includes(selectedAsrModel) ? [selectedAsrModel, ...asrModels] : asrModels;

  return (
    <ModalWrapper width="800px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Set ASR and LLM models for organization</ModalTitle>
      <Content>
        <section className="mb-6 border-b border-[var(--color-border)] pb-6">
          <h3 className="mb-3 text-base font-semibold text-[var(--color-text)]">ASR model</h3>
          <div className="grid grid-cols-[minmax(0,1fr)_120px] items-end gap-3">
            <Select
              value={selectedAsrModel}
              options={[
                {
                  label: asrDefaultModel ? 'default (' + asrDefaultModel + ')' : 'default',
                  value: '',
                },
                ...asrModelOptions.map((value) => ({ label: value, value })),
              ]}
              onChange={(_, value) => setSelectedAsrModel(value)}
              label="ASR model"
              required
              name="asr-model"
              disabled={isLoading}
            />
            <OutlinedButton
              className="btn--md w-full"
              onClick={() => updateAsrModelForOrg(selectedAsrModel)}
              disabled={isLoading || selectedAsrModel === asrModel}
            >
              Set
            </OutlinedButton>
          </div>
        </section>

        <section>
          <h3 className="mb-3 text-base font-semibold text-[var(--color-text)]">LLM prompt models</h3>
          <div className="mb-5 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_248px] items-end gap-3">
            <Select
              value={bulkModel}
              options={versions.map((value) => ({ label: value, value }))}
              onChange={(_, value) => setBulkModel(value)}
              label="Model for all features"
              required
              name="bulk-model"
              disabled={isLoading || !versions.length}
            />
            <div className="flex gap-2">
              <OutlinedButton
                className="btn--md flex-1"
                onClick={() => handleBulkUpdate(bulkModel)}
                disabled={isLoading || !bulkModel}
              >
                Set all
              </OutlinedButton>
              <OutlinedButton className="btn--md flex-1" onClick={() => handleBulkUpdate('')} disabled={isLoading}>
                Unset all
              </OutlinedButton>
            </div>
          </div>

          <DisclosureSection
            title="Advanced settings"
            open={advancedOpen}
            setOpenState={setAdvancedOpen}
            loading={isLoading}
            className="advancedSection"
            titleClassName="!bg-[var(--color-surface)]"
          >
            <div className="flex flex-col w-full">
              {allPrompts.map((prompt) => (
                <div key={prompt.slug} className="promptRow">
                  <div className="promptRow__slug">{prompt.slug}</div>
                  <div className="promptRow__select">
                    <Select
                      value={promptsConfigMap.get(prompt.slug) ?? ''}
                      options={[{ label: 'default', value: '' }, ...versions.map((value) => ({ label: value, value }))]}
                      onChange={async (_, value) => {
                        setIsLoading(true);
                        toast.loading('Checking if model is applicable for this feature...', { id: 'model-check' });

                        try {
                          const isUpdated = await updatePromptModelForOrg(prompt.slug, value);
                          if (!isUpdated) {
                            return;
                          }

                          setPromptsConfigMap((prev) => {
                            const updated = new Map(prev);
                            updated.set(prompt.slug, value);
                            return updated;
                          });
                          toast.success('Model updated successfully');
                        } finally {
                          toast.remove('model-check');
                          setIsLoading(false);
                        }
                      }}
                      label="Model"
                      required
                      name="model"
                    />
                  </div>
                </div>
              ))}
            </div>
          </DisclosureSection>
        </section>

        <div className="buttonGroup">
          <OutlinedButton onClick={closeModal}>Close</OutlinedButton>
        </div>
      </Content>
    </ModalWrapper>
  );
};
